import React, { useState, useRef, useEffect } from 'react'
import { X, CheckCircle, FileCheck, MessageSquare, Database, Sparkles } from 'lucide-react'
import { SaleWizard } from './SaleWizard'
import { Button } from '../ui/Button'
import { Progress } from '../ui/ProgressBar'
import { cn } from '@/lib/utils'
import toast from 'react-hot-toast'
import { useHubData } from '../../hooks/useHubData'
import { useHubAccess } from '../../hooks/useHubAccess'
import { useAuth } from '../../contexts/AuthContext'

type ExchangeStep = 'identification-form'

export const ExchangeHub: React.FC<{ onClose: () => void; onSectionComplete?: () => void }> = ({ onClose, onSectionComplete }) => {
  const { user } = useAuth()
  const [exchangeStep, setExchangeStep] = useState<ExchangeStep>('identification-form')
  const [isStepComplete, setIsStepComplete] = useState(false)
  const [isIdentificationHubSubmitted, setIsIdentificationHubSubmitted] = useState(() => localStorage.getItem('identification_hub_submitted') === 'true')
  const [showTicketModal, setShowTicketModal] = useState(false)
  const [ticketMessage, setTicketMessage] = useState('')
  const controlsRef = useRef<any | null>(null)
  const { getData } = useHubData('initial')
  const access = useHubAccess('identification', user)
  
  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])
  
  useEffect(() => {
    const updateCompletion = () => {
      if (!controlsRef.current?.isStepComplete) return
      const complete = controlsRef.current.isStepComplete(6)
      setIsStepComplete(complete)
    }
    
    const timeout = setTimeout(updateCompletion, 100)
    const interval = setInterval(updateCompletion, 1000)
    
    return () => {
      clearTimeout(timeout)
      clearInterval(interval)
    }
  }, [exchangeStep])

  // Listen for identification hub completion events
  useEffect(() => {
    const handleIdentificationHubCompleted = () => {
      setIsIdentificationHubSubmitted(true)
    }
    window.addEventListener('identificationHubCompleted', handleIdentificationHubCompleted)
    return () => window.removeEventListener('identificationHubCompleted', handleIdentificationHubCompleted)
  }, [])

  // Check localStorage periodically for submission status
  useEffect(() => {
    const checkSubmission = () => {
      const submitted = localStorage.getItem('identification_hub_submitted') === 'true'
      if (submitted !== isIdentificationHubSubmitted) {
        setIsIdentificationHubSubmitted(submitted)
      }
    }
    checkSubmission()
    const interval = setInterval(checkSubmission, 500)
    return () => clearInterval(interval)
  }, [isIdentificationHubSubmitted])

  const handleSaveProgress = () => {
    const success = controlsRef.current?.saveProgress?.()
    if (success) {
      toast.success('Progress saved successfully!')
    } else {
      toast.error('Failed to save progress. Please try again.')
    }
  }

  const handleRequestEdit = () => {
    if (!isIdentificationHubSubmitted) {
      toast.error('Please submit the Identification Hub section before requesting edits')
      return
    }
    setShowTicketModal(true)
  }

  const handleSubmitTicket = () => {
    if (!ticketMessage.trim()) {
      toast.error('Please enter a description of the changes needed')
      return
    }
    
    const ticketNumber = `TICKET-${Date.now().toString().slice(-6)}`
    toast.success(`Edit request submitted! Ticket #${ticketNumber}`)
    
    const tickets = JSON.parse(localStorage.getItem('edit_tickets') || '[]')
    tickets.push({
      ticketNumber,
      message: ticketMessage,
      status: 'pending',
      createdAt: new Date().toISOString(),
      section: 'Identification Hub'
    })
    localStorage.setItem('edit_tickets', JSON.stringify(tickets))
    
    setTicketMessage('')
    setShowTicketModal(false)
  }

  const handleComplete = () => {
    setIsStepComplete(true)
    onSectionComplete?.()
  }

  const initialData = {
    exchangerPhaseOne: getData('exchangerPhaseOne'),
    relinquished: getData('relinquished'),
    identificationForm: getData('identificationForm')
  }

  if (!access.canAccess) {
    return (
      <div className="modal-overlay">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md p-6 my-4">
          <div className="text-center">
            <FileCheck className="h-12 w-12 text-purple-600 dark:text-purple-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">Identification Hub Locked</h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
              {access.message || 'Please complete the Sales Hub first'}
            </p>
            <Button onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="modal-overlay">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[calc(100vh-8rem)] overflow-hidden flex flex-col my-4">
        {/* Header */}
        <div className="border-b border-slate-200 dark:border-slate-700 px-6 py-4 bg-white dark:bg-slate-800">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <FileCheck className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              <div>
                <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Identification Hub</h2>
                <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">Identify replacement properties within 45 days.</p>
                {isIdentificationHubSubmitted && (
                  <div className="flex items-center space-x-2 mt-1">
                    <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <span className="text-xs text-green-600 dark:text-green-400 font-medium">Completed & Read-Only</span>
                  </div>
                )}
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-secondary-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
              <X className="h-5 w-5 text-secondary-600 dark:text-slate-400" />
            </button>
          </div>
          {/* Completion Progress */}
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-600 dark:text-slate-400">Completion</span>
              <span className="text-xs font-semibold text-purple-600 dark:text-purple-400">{access.completionPercent}%</span>
            </div>
            <Progress value={access.completionPercent} className="h-2 [&>div]:!bg-gradient-to-r [&>div]:!from-purple-600 [&>div]:!via-purple-500 [&>div]:!to-purple-600 [&>div]:!shadow-[0_0_10px_rgba(147,51,234,0.6)]" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden p-4 bg-white dark:bg-slate-800 flex flex-col">
          {/* Step Navigation */}
          <div className="mb-3 border-b border-slate-200 dark:border-slate-700 pb-3 flex-shrink-0">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">During Exchange</h3>
              <span className="text-xs text-slate-500 dark:text-slate-500">
                Step 1 of 1
              </span>
            </div>
            <div className="flex items-center space-x-1.5">
              <button
                type="button"
                onClick={() => setExchangeStep('identification-form')}
                className={cn(
                  "flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all",
                  exchangeStep === 'identification-form'
                    ? "bg-purple-600 dark:bg-purple-600 text-white shadow-sm"
                    : isStepComplete
                    ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30"
                    : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600"
                )}
              >
                <FileCheck className="h-3.5 w-3.5" />
                <span>Identification Form</span>
                {isStepComplete && <CheckCircle className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-scroll" style={{ maxHeight: '100%' }}>
            <SaleWizard 
              embedded 
              hideFooter 
              exposeControls={(api) => (controlsRef.current = api)} 
              mode="exchange" 
              onClose={onClose} 
              onComplete={handleComplete}
              initialData={initialData}
            />
          </div>
        </div>

        {/* Footer - global navigation */}
        <div className="border-t border-slate-200 dark:border-slate-700 px-6 py-4 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900">
          <div className="flex items-center justify-between">
            {isIdentificationHubSubmitted ? (
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400" />
                <div>
                  <p className="text-sm font-semibold text-green-700 dark:text-green-300">Identification Hub Completed</p>
                  <p className="text-xs text-green-600 dark:text-green-400">
                    This section has been submitted and is now read-only.
                  </p>
                </div>
              </div>
            ) : (
              <Button 
                onClick={handleRequestEdit}
                disabled={true}
                variant="outline"
                className="border-slate-300 text-slate-400 dark:border-slate-600 dark:text-slate-500 cursor-not-allowed opacity-50"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Request Edit
              </Button>
            )}
            <div className="flex items-center space-x-3">
              {!isIdentificationHubSubmitted && (
                <Button 
                  onClick={handleSaveProgress}
                  variant="outline"
                  className="group relative border-2 border-slate-300 dark:border-slate-600 hover:border-purple-400 dark:hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 shadow-sm hover:shadow-md transition-all duration-200"
                >
                  <Database className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" />
                  Save Progress
                </Button>
              )}
              <Button 
                className="group relative bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 dark:from-purple-600 dark:to-purple-500 dark:hover:from-purple-700 dark:hover:to-purple-600 text-white shadow-lg hover:shadow-xl border-0 transition-all duration-200" 
                onClick={() => {
                  console.log('Submit button clicked', { controlsRef: controlsRef.current, isIdentificationHubSubmitted })
                  try {
                    if (controlsRef.current?.submit) {
                      console.log('Calling submit function')
                      controlsRef.current.submit()
                    } else {
                      console.error('Submit function not available', controlsRef.current)
                      toast.error('Wizard not ready. Please wait a moment and try again.')
                    }
                  } catch (error) {
                    console.error('Error submitting identification hub:', error)
                    toast.error('Failed to submit. Please try again.')
                  }
                }}
                disabled={isIdentificationHubSubmitted}
              >
                <Sparkles className="h-4 w-4 mr-2 group-hover:rotate-12 transition-transform" />
                {isIdentificationHubSubmitted ? 'Submitted' : 'Submit'}
              </Button>
              {isIdentificationHubSubmitted && (
                <Button 
                  onClick={handleRequestEdit}
                  variant="outline"
                  className="group relative border-2 border-[#69adba]/30 text-[#69adba] hover:border-[#69adba] hover:bg-[#69adba]/10 dark:border-[#69adba]/40 dark:text-[#69adba] dark:hover:border-[#69adba] dark:hover:bg-[#69adba]/20 hover:shadow-md transition-all duration-200"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Request Edit
                </Button>
              )}
              <Button 
                onClick={onClose} 
                variant="outline"
                className="group relative border-2 border-slate-300 dark:border-slate-600 hover:border-red-400 dark:hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 shadow-sm hover:shadow-md transition-all duration-200"
              >
                <X className="h-4 w-4 mr-2 group-hover:rotate-90 transition-transform" />
                Close
              </Button>
            </div>
          </div>
        </div>

        {/* Edit Request Ticket Modal */}
        {showTicketModal && (
          <div className="modal-overlay modal-overlay-nested">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden">
              {/* Header */}
              <div className="border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between bg-gradient-to-r from-[#69adba]/10 to-blue-500/10 dark:from-[#69adba]/20 dark:to-blue-500/20">
                <div className="flex items-center space-x-3">
                  <MessageSquare className="h-5 w-5 text-[#69adba] dark:text-[#69adba]" />
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Request Edit</h3>
                </div>
                <button 
                  onClick={() => {
                    setShowTicketModal(false)
                    setTicketMessage('')
                  }}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                </button>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                  Please describe the changes you need to make to the Identification Hub:
                </p>
                <textarea
                  value={ticketMessage}
                  onChange={(e) => setTicketMessage(e.target.value)}
                  placeholder="Describe the changes needed..."
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#69adba] dark:bg-slate-700 dark:text-slate-100 resize-none"
                  rows={6}
                />
              </div>
              
              {/* Footer */}
              <div className="border-t border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-end space-x-3 bg-slate-50 dark:bg-slate-800/50">
                <Button
                  onClick={() => {
                    setShowTicketModal(false)
                    setTicketMessage('')
                  }}
                  variant="outline"
                  className="border-slate-300 dark:border-slate-600"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitTicket}
                  disabled={!ticketMessage.trim()}
                  className="bg-gradient-to-r from-[#69adba] to-blue-500 hover:from-[#5a9aa6] hover:to-blue-600 dark:from-[#69adba] dark:to-blue-500 dark:hover:from-[#5a9aa6] dark:hover:to-blue-600 text-white border-0 shadow-lg"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Submit Request
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

